import { UserId } from '@cibo/core'
import { useAuth } from '@cibo/profile'
import { ResponsiveDialog, SubmitButton } from '@cibo/ui'
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { Form, Formik, useFormikContext } from 'formik'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useCreateFarm, useFarm, useUpdateFarm } from '../../../../../queries'
import { FarmFormControls } from './FarmFormControls'

type FarmFormModalProps = {
  open: boolean
  onCancel(): void
  onSuccess(result: any): void
  farmId?: string
  ownerId?: UserId
}

export const FarmFormDialogImpl = ({
  disableOwnerSelection,
  open,
  onCancel,
  onSuccess,
}: FarmFormModalProps & { disableOwnerSelection: boolean }) => {
  const { t } = useTranslation('@cibo/landmanager/LandManager/FarmForm')
  const { resetForm } = useFormikContext()

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <ResponsiveDialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('editFarm')}</DialogTitle>

      <DialogContent>
        <FarmFormControls disableOwnerSelection={disableOwnerSelection} />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <SubmitButton testId="save-farm">{t('save')}</SubmitButton>
      </DialogActions>
    </ResponsiveDialog>
  )
}

export const FarmFormDialog = (props: FarmFormModalProps) => {
  const { t } = useTranslation('@cibo/landmanager/LandManager/FarmForm')
  const { enqueueSnackbar } = useSnackbar()
  const { userId: selfUserId } = useAuth()
  const farm = useFarm(props.farmId)

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t('required')).max(255, t('max')),
        owner: yup.number().required(),
      }),
    []
  )
  const createFarm = useCreateFarm()
  const updateFarm = useUpdateFarm()

  const handleSubmitForm = (values: any) => {
    const { address } = values

    const request = {
      id: props.farmId,
      name: values.name,
      address: address && {
        street: address.street,
        city: address.city,
        postalCode: address.postalCode,
        state: address.state?.label || address.state,
        country: address.country?.name,
        countryCode: address.country?.code,
      },
      owner: values.owner,
    }

    const mutate = props.farmId ? updateFarm : createFarm

    return mutate
      .mutateAsync(request)
      .then((result: any) => {
        enqueueSnackbar(t('success', request), {
          variant: 'success',
        })

        props.onSuccess && props.onSuccess(result)
      })
      .catch(error => {
        enqueueSnackbar(`${t('errorOnSave')}`, {
          variant: 'error',
        })

        throw error
      })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={farm.data || { owner: props.ownerId || selfUserId }}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
    >
      <Form>
        <FarmFormDialogImpl {...props} disableOwnerSelection={!!props.ownerId} />
      </Form>
    </Formik>
  )
}
